import { AxiosResponse } from "axios";
import { api } from ".";
import { ICmTableDataMediaItem, ICmTableDataRelation, INormalisedRelations, PrimaryImage, RelationshipEditorState } from "../types";
import { isSpatialDatasetSlug, SPATIAL_DATASETS_TABLE, TABULAR_DATASETS_TABLE } from "../utilities/configAdapter";

export type CmTableSpatialType = "point" | "polygon" | "linestring" | "none";

/**
 * Raw spatial type as it may arrive from the backend API (e.g. "multipolygon",
 * "multilinestring") or config. "linestring" is the canonical line type —
 * "line" is no longer accepted. Use `normaliseSpatialType()` to collapse
 * multi-variants into the base `CmTableSpatialType` before passing to UI
 * components like DrawingTools.
 */
export type CmTableSpatialTypeRaw = CmTableSpatialType | "multipoint" | "multipolygon" | "multilinestring";

/**
 * Collapses multi-variants into the base spatial type used by DrawingTools,
 * CmTableDrawMap, and map layer specs.
 * - multipolygon     → polygon
 * - multilinestring  → linestring
 * - multipoint       → point
 * - unknown/undefined → none
 */
export function normaliseSpatialType(raw: CmTableSpatialTypeRaw | string | null | undefined): CmTableSpatialType {
  switch (raw) {
    case "polygon":
    case "multipolygon":
      return "polygon";
    case "linestring":
    case "multilinestring":
      return "linestring";
    case "point":
    case "multipoint":
      return "point";
    default:
      return "none";
  }
}

// ─── CM Table Data (row list) ──────────────────────────────────────────────

export function normaliseRelations(raw: any | undefined): INormalisedRelations {
  if (!raw) return { byTableSlug: {}, media: [] };

  // Idempotency: already normalised — return as-is
  if (raw.byTableSlug) return raw;

  // The API keys relationships by table slug at the top level, e.g.
  // { "media": [...], "org999_culsites": [...] }. Anchor-type keys (e.g.
  // "spatialdataset") are mapped to the table slug the relationship editor and
  // column builder use (e.g. "spatial-datasets"). Tabular datasets
  // (dataset_type='tabular') come back under their own "tabulardataset" key.
  const anchorTypeToTableSlug: Record<string, string> = {
    spatialdataset: SPATIAL_DATASETS_TABLE,
    tabulardataset: TABULAR_DATASETS_TABLE
  };

  const byTableSlug: Record<string, ICmTableDataRelation[]> = {};
  for (const [key, items] of Object.entries(raw)) {
    if (key === "media" || !Array.isArray(items)) continue;
    byTableSlug[anchorTypeToTableSlug[key] ?? key] = items as ICmTableDataRelation[];
  }
  return {
    byTableSlug,
    media: Array.isArray(raw.media) ? raw.media : []
  };
}

/**
 * Canonical adapter that converts backend wire-format row data into a stable internal shape.
 * Single boundary for all V2 row transformations: field promotion, primary_image normalisation,
 * relationships normalisation, and primary_image enrichment from media.
 */
export function normaliseCmTableRow(row: any): ICmTableDataRow {
  if (!row) return row;

  // Resolve the primary media URL — prefer primary_image, fall back to thumbnail/image.
  // The primary media may be a non-image (e.g. a video), so its URL is kept separate
  // from the display thumbnail resolved below.
  const rawImage = row.primary_image ?? row.thumbnail ?? row.image ?? null;
  const primaryUrl = typeof rawImage === "string" ? rawImage : (rawImage?.url ?? null);

  // Accept both field names for backward compatibility with cached pre-migration rows
  const rawRelationships = row.relationships ?? row.relations;
  const normalisedRelationships = normaliseRelations(rawRelationships);
  const primaryMedia = normalisedRelationships.media.find((m: any) => m.is_primary === true);

  // Resolve the display thumbnail image. Prefer an explicit top-level `thumbnail`
  // (the backend's real poster image, which differs from `primary_image` when the
  // primary media is a video), then any thumbnail nested on primary_image, then the
  // is_primary media item's thumbnail, and finally fall back to the primary media URL.
  const topLevelThumb: string | null = typeof row.thumbnail === "string" ? row.thumbnail : (row.thumbnail?.url ?? null);
  const nestedPrimaryThumb: string | null = rawImage && typeof rawImage === "object" ? (rawImage.thumbnail ?? null) : null;
  const mediaThumb: string | null = (primaryMedia as any)?.thumbnail ?? null;
  const thumbnailUrl = topLevelThumb ?? nestedPrimaryThumb ?? mediaThumb ?? primaryUrl;

  // Build canonical primary_image, then enrich id/name/description from the is_primary media item
  let primaryImage: any = primaryUrl ? { id: null, url: primaryUrl, thumbnail: thumbnailUrl, name: "", description: "" } : null;
  if (primaryImage && primaryMedia) {
    primaryImage = {
      ...primaryImage,
      id: primaryMedia.id,
      name: primaryMedia.name || "",
      description: (primaryMedia as any).entity_description || ""
    };
  }

  // Exclude name, description AND id — row_data.id is a domain key (e.g. "hs_10"),
  // never the row's DB pk, so it must not overwrite the top-level numeric `id`.
  const { name: _rowDataName, description: _rowDataDescription, id: _rowDataId, ...promotableRowData } = row.row_data ?? {};

  return {
    ...row,
    ...promotableRowData,
    primary_image: primaryImage,
    thumbnail: thumbnailUrl,
    relationships: normalisedRelationships,
    relations: normalisedRelationships
  };
}

export interface ICmTableDataRow {
  id: number;
  uuid: string;
  name: string;
  description: string;
  table: number;
  table_name: string;
  table_slug: string;
  row_data: Record<string, any>;
  geometry: object | null;
  primary_image: PrimaryImage | null;
  thumbnail: string | null;
  organisation: number;
  user: number;
  restrictions: any[];
  created_at: string;
  last_updated_at: string;
  relations?: INormalisedRelations;
  relationships?: INormalisedRelations; // Normalized V2 shape
}

export interface ICmTableDataResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ICmTableDataRow[];
}

/**
 * Fetches rows for a CM Table identified by its slug.
 */
export const getCmTableData = async (
  tableSlug: string,
  params?: { page?: number; page_size?: number; search?: string; include_relations?: boolean; bbox?: string }
): Promise<AxiosResponse<ICmTableDataResponse>> => {
  const response = await api.get(`v2/tables/${tableSlug}/rows/`, {
    params: {
      // Default false — list calls don't need relationship data; only detail calls do.
      ...(params?.include_relations ? { include_relations: true } : {}),
      ...(params?.page ? { page: params.page } : {}),
      ...(params?.page_size ? { page_size: params.page_size } : {}),
      ...(params?.search ? { search: params.search } : {}),
      ...(params?.bbox ? { bbox: params.bbox } : {})
    }
  });

  // Apply canonical normalization to all rows
  if (response.data?.results) {
    response.data.results = response.data.results.map((row: any) => normaliseCmTableRow(row));
  }

  return response;
};

/**
 * Fetches a single row from a CM Table.
 * Endpoint: GET /api/v2/tables/{slug}/rows/{id}/
 */
export const getCmTableRow = async (
  tableSlug: string,
  rowId: string | number,
  params?: { include_relations?: boolean }
): Promise<AxiosResponse<ICmTableDataRow>> => {
  const response = await api.get(`v2/tables/${tableSlug}/rows/${rowId}/`, {
    params: {
      include_relations: params?.include_relations ?? true
    }
  });

  if (response.data) {
    response.data = normaliseCmTableRow(response.data);
  }

  return response;
};

// ─── CM Table GeoJSON ──────────────────────────────────────────────────────

export interface ICmTableGeoFeature {
  type: "Feature";
  geometry: {
    type: string;
    coordinates: any;
  };
  properties: {
    id: number;
    name: string;
    description: string;
    thumbnail: string | null;
    [key: string]: any;
  };
}

export interface ICmTableGeoResponse {
  type: "FeatureCollection";
  table_id: number;
  table_name: string;
  table_slug: string;
  spatial_type: CmTableSpatialType;
  count: number;
  features: ICmTableGeoFeature[];
}

// ─── Anchor Relationships ─────────────────────────────────────────────────────

export type AnchorType = "cmtabledata" | "media" | "spatialdataset";

export interface IAnchorRelationshipItem {
  to_type: AnchorType;
  to_object_id: number;
}

/**
 * `PATCH anchor-relationships/<type>/<id>/` response: the anchor's relationships
 * after the change, grouped like `include_relations` (by table slug, plus
 * `media`, which is always present).
 */
export interface IAnchorRelationshipsResponse {
  count: number;
  relationships: {
    media: ICmTableDataMediaItem[];
    [groupKey: string]: ICmTableDataRelation[] | ICmTableDataMediaItem[];
  };
}

export interface IAnchorRelationshipsPatchPayload {
  /** New relationships to create — `to_object_id` of the row/media being linked. */
  create: IAnchorRelationshipItem[];
  /** `relationship_id`s of existing relationships to remove. */
  delete: number[];
}

/**
 * Diff-applies relationship changes for an anchor — only the additions and
 * removals are sent, unlike a full replace.
 * Endpoint: PATCH /api/v2/anchor-relationships/{type}/{id}/
 */
export const patchAnchorRelationships = async (
  anchorType: AnchorType,
  anchorId: number | string,
  payload: IAnchorRelationshipsPatchPayload
): Promise<AxiosResponse<IAnchorRelationshipsResponse>> => {
  return api.patch(`v2/anchor-relationships/${anchorType}/${anchorId}/`, payload);
};

/**
 * Anchor type for a relationship-editor bucket key (an endpoint's `tableName`).
 * The spatial-dataset warehouse and the synthesized Tabular data tab are both
 * SpatialDatasets (the backend reads tabular links out as "tabulardataset" but
 * only accepts "spatialdataset" as a write type); every other table key is a CM table.
 */
export const relationshipSlugToAnchorType = (slug: string): AnchorType => {
  if (slug === "media") return "media";
  if (isSpatialDatasetSlug(slug) || slug === TABULAR_DATASETS_TABLE || slug === "tabulardataset") return "spatialdataset";
  return "cmtabledata";
};

/**
 * Diffs an "original" relationship snapshot against the current editor state and
 * builds the `{ create, delete }` payload for `patchAnchorRelationships`.
 *
 * - `create`: items in `current` without a `relationship_id` (freshly selected —
 *   see `RelationshipEditorTable`, which seeds new selections with `relationship_id: null`).
 * - `delete`: `relationship_id`s present in `original` but no longer present in `current`.
 */
export const buildAnchorRelationshipsPatchPayload = (
  original: RelationshipEditorState,
  current: RelationshipEditorState
): IAnchorRelationshipsPatchPayload => {
  const keptRelationshipIds = new Set<number>();
  const create: IAnchorRelationshipItem[] = [];

  for (const [slug, items] of Object.entries(current)) {
    for (const item of items) {
      if (item.relationship_id) {
        keptRelationshipIds.add(item.relationship_id);
      } else {
        create.push({ to_type: relationshipSlugToAnchorType(slug), to_object_id: item.id });
      }
    }
  }

  const del: number[] = [];
  for (const items of Object.values(original)) {
    for (const item of items) {
      if (item.relationship_id && !keptRelationshipIds.has(item.relationship_id)) {
        del.push(item.relationship_id);
      }
    }
  }

  return { create, delete: del };
};

// ─── CM Table Media Relationships ────────────────────────────────────────────

/**
 * Creates a relationship between a CM table row and a media item.
 * Endpoint: POST /api/v2/relationships/
 */
export const createCmTableMediaRelationship = async (
  fromObjectId: number | string,
  toObjectId: number,
  isPrimary: boolean = false
): Promise<AxiosResponse> => {
  return api.post("v2/relationships/", {
    from_type: "cmtabledata",
    from_object_id: fromObjectId,
    to_type: "media",
    to_object_id: toObjectId,
    is_primary: isPrimary
  });
};

/**
 * Sets the primary image for a CM table row.
 * Endpoint: POST /api/v2/media/:pk/set-primary-image/
 */
export const setCmTablePrimaryImage = async (mediaId: number, cmTableDataId: number | string): Promise<AxiosResponse> => {
  return api.post(`v2/media/${mediaId}/set-primary-image/`, {
    cmtabledata: cmTableDataId
  });
};

/**
 * Creates a relationship between a Spatial Dataset and a media item.
 * Endpoint: POST /api/v2/relationships/
 */
export const createSpatialDatasetMediaRelationship = async (
  fromObjectId: number | string,
  toObjectId: number,
  isPrimary: boolean = false
): Promise<AxiosResponse> => {
  return api.post("v2/relationships/", {
    from_type: "spatialdataset",
    from_object_id: fromObjectId,
    to_type: "media",
    to_object_id: toObjectId,
    is_primary: isPrimary
  });
};

/**
 * Sets the primary image for a Spatial Dataset.
 * Endpoint: POST /api/v2/media/:pk/set-primary-image/
 */
export const setSpatialDatasetPrimaryImage = async (mediaId: number, spatialDatasetId: number | string): Promise<AxiosResponse> => {
  return api.post(`v2/media/${mediaId}/set-primary-image/`, {
    spatialdataset: spatialDatasetId
  });
};

/**
 * Deletes a v2 relationship by its relationship_id.
 * Endpoint: DELETE /api/v2/relationships/{id}/
 * Used to unlink or remove media from a CM Table row / Spatial Dataset without globally deleting the media item.
 */
export const deleteCmTableMediaRelationship = async (relationshipId: number): Promise<void> => {
  await api.delete(`v2/relationships/${relationshipId}/`);
};

/**
 * Updates fields on a CM Table row.
 * Endpoint: PATCH /api/v2/tables/{slug}/rows/{id}/
 */
export const updateCmTableRow = async (
  tableSlug: string,
  rowId: string | number,
  data: { name?: string; description?: string; geometry?: GeoJSON.Geometry; row_data?: Record<string, any>; restrictions?: number[] }
): Promise<AxiosResponse<ICmTableDataRow>> => {
  const response = await api.patch(`v2/tables/${tableSlug}/rows/${rowId}/`, data);
  if (response.data) {
    // Defensive: unwrap {message, data: {...row}} if backend wraps PATCH responses
    const rowData = response.data?.data ?? response.data;
    response.data = normaliseCmTableRow(rowData);
  }
  return response;
};

/**
 * Creates a new row in a CM Table.
 * Endpoint: POST /api/v2/tables/{slug}/rows/
 */
export const createCmTableRow = async (
  tableSlug: string,
  data: {
    name: string;
    description?: string;
    geometry?: GeoJSON.Geometry;
    row_data?: Record<string, any>;
  }
): Promise<AxiosResponse<ICmTableDataRow>> => {
  const response = await api.post(`v2/tables/${tableSlug}/rows/`, data);
  if (response.data) {
    // Backend wraps create response in {message: "Row created successfully", data: {...row}}
    const rowData = response.data?.data ?? response.data;
    response.data = normaliseCmTableRow(rowData);
  }
  return response;
};

/**
 * Deletes a row from a CM Table.
 * Endpoint: DELETE /api/v2/tables/{slug}/rows/{id}/
 */
export const deleteCmTableRow = async (tableSlug: string, rowId: string | number): Promise<AxiosResponse<void>> => {
  return api.delete(`v2/tables/${tableSlug}/rows/${rowId}/`);
};

/**
 * Updates table-level configuration (name, spatial type, field_schema choices
 * and/or map_display_config). Endpoint: PATCH /api/v2/tables/{slug}/
 *
 * Used by the inline "add/edit styling choice" flow and by the admin console's
 * per-table editor. Superusers are org-less, so the backend's superuser body
 * override targets the right tenant — but it reads **`organisation_id`** only
 * (`org_utils.py:46`), the same key restrictions use. A bare `organisation` is
 * silently ignored and the request falls back to the caller's profile org,
 * which for a superuser means a 404 or a 400.
 */
export const updateCmTable = async (
  tableSlug: string,
  data: {
    name?: string;
    description?: string;
    spatial_type?: string;
    field_schema?: Array<{ name: string; type: string; label?: string; choices?: string[] }> | Record<string, any>[];
    map_display_config?: { style?: Record<string, any>; [key: string]: any };
    extras?: Record<string, any>;
    organisation_id?: number | null;
  }
): Promise<AxiosResponse<any>> => {
  return api.patch(`v2/tables/${tableSlug}/`, data);
};

/**
 * Creates a CM Table on an existing organisation.
 * Endpoint: POST /api/v2/tables/
 *
 * Until now a table could only be created while provisioning a brand-new org
 * (`POST /api/v2/admin/provision-org/`, superuser-only and atomic), so an org
 * admin had no way to add one afterwards.
 *
 * `slug` is left to the backend to derive from `name` — it namespaces per org
 * (`org999_place`), which the client cannot construct safely. Like
 * {@link updateCmTable}, org targeting is **`organisation_id`** and nothing else:
 * a superuser is org-less, and a bare `organisation` is silently ignored.
 */
export const createCmTable = async (data: {
  name: string;
  spatial_type: string;
  description?: string;
  field_schema?: Array<{ name: string; type: string; label?: string; choices?: string[] }> | Record<string, any>[];
  map_display_config?: { style?: Record<string, any>; [key: string]: any };
  organisation_id?: number | null;
}): Promise<AxiosResponse<{ slug: string; name: string; spatial_type: string }>> => {
  return api.post("v2/tables/", data);
};

/**
 * Fetches a CM Table's definition (incl. `field_schema` + `spatial_type`).
 * Endpoint: GET /api/v2/tables/{slug}/
 */
export const getCmTable = async (
  tableSlug: string
): Promise<
  AxiosResponse<{
    slug: string;
    name: string;
    spatial_type: string;
    field_schema: Array<{ name: string; type: string; label?: string; choices?: string[] }>;
  }>
> => {
  return api.get(`v2/tables/${tableSlug}/`);
};

/**
 * Fetches GeoJSON FeatureCollection for a spatial CM Table identified by slug.
 * Supports optional bbox string "minLng,minLat,maxLng,maxLat" for viewport filtering
 * and row IDs for exporting a selected set of records.
 */
/**
 * The CM-table GeoJSON as a file-ready Blob: the response bytes as the server
 * sent them, never parsed. For the Offline export; the map uses getCmTableGeo.
 */
export const getCmTableGeojsonFile = async (
  tableSlug: string,
  opts: { rowIds?: number[]; timeout?: number; onProgress?: (loadedBytes: number) => void } = {}
): Promise<Blob> => {
  const res = await api.get<Blob>(`v2/tables/${tableSlug}/geojson/`, {
    responseType: "blob",
    timeout: opts.timeout,
    params: opts.rowIds?.length ? { row_ids: opts.rowIds.join(",") } : {},
    onDownloadProgress: opts.onProgress ? (e) => opts.onProgress?.(e.loaded) : undefined
  });
  return res.data;
};

export const getCmTableGeo = async (
  tableSlug: string,
  params?: { search?: string; bbox?: string; rowIds?: number[] },
  opts?: { timeout?: number }
): Promise<AxiosResponse<ICmTableGeoResponse>> => {
  return api.get(`v2/tables/${tableSlug}/geojson/`, {
    // Whole-table exports run to megabytes and will not clear the 10 s client
    // default; callers that expect one pass their own budget. Left undefined the
    // global default still applies, so a map click keeps failing fast.
    timeout: opts?.timeout,
    params: {
      ...(params?.search ? { search: params.search } : {}),
      ...(params?.bbox ? { bbox: params.bbox } : {}),
      ...(params?.rowIds?.length ? { row_ids: params.rowIds.join(",") } : {})
    }
  });
};

/**
 * Narrows a tokenized GeoJSON URL to the supplied CM-table row IDs. The
 * download token still authorizes the table; `row_ids` only filters features.
 */
export const withRowIds = (url: string, rowIds?: number[]): string => {
  if (!rowIds?.length) return url;

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}row_ids=${encodeURIComponent(rowIds.join(","))}`;
};

// ─── CM Table Bulk GeoJSON ─────────────────────────────────────────────────

export interface ICmTableBulkGeoResponse {
  type: "FeatureCollection";
  table_ids: number[];
  table_count: number;
  count: number;
  features: ICmTableGeoFeature[];
}

/**
 * Fetches a merged GeoJSON FeatureCollection for multiple spatial CM Tables
 * within the given viewport bounding box.
 *
 * Uses the v2 ?slugs= param (preferred over legacy ?table_ids=).
 * Each feature includes `table_id`, `table_slug`, and `spatial_type` in its
 * properties so the rendering layer can group features by table.
 */
export const getCmTableBulkGeo = async (tableSlugs: string[], bbox?: string): Promise<AxiosResponse<ICmTableBulkGeoResponse>> => {
  return api.get("v2/geojson/bulk/", {
    params: {
      slugs: tableSlugs.join(","),
      ...(bbox ? { bbox } : {})
    }
  });
};

// ─── QGIS Download Token ──────────────────────────────────────────────────────
// POST /api/v2/tables/{slug}/download-token/
// Returns 201 in the standard v2 envelope. The tokenized GeoJSON URL is at
// data.url — this is the URL that goes into the .qlr <datasource>.
// Lifetime: 8 h, revocable on logout.
// See docs/developer-guides/QGIS_EXPORT_FRONTEND_CONTRACT.md §2.

export interface CmTableDownloadTokenResponse {
  status: string;
  message: string;
  data: {
    url: string; // complete tokenized GeoJSON URL, ready to drop into .qlr
    expires_in: number; // seconds (28800 = 8 h)
    expires_at: string; // ISO-8601 UTC
  };
}

/**
 * Mints a short-lived download token for the given CM Table slug and returns
 * the tokenized GeoJSON URL. QGIS can load this URL without an Authorization
 * header for 8 hours. The token is revoked when the minting user logs out.
 */
export const createCmTableDownloadToken = async (tableSlug: string): Promise<CmTableDownloadTokenResponse> => {
  const res = await api.post<CmTableDownloadTokenResponse>(`v2/tables/${tableSlug}/download-token/`);
  return res.data;
};
